/**
 * Screen 9 — Showcase Validator（展示关卡回归验证）
 *
 * 访问: #/showcase-validator
 * 自动运行 4 关 headless 对打，展示 P0/P1/P2 三层验收结果。
 */

import { pathFor } from "../router/screen-router";

/*
 *
 * 用法:
 *   pnpm dev → 浏览器打开 http://localhost:5173/#/showcase-validator
 */

import { validateShowcaseLevel, type ShowcaseReport } from "../services/showcase-validator";
import assertionsConfig from "../services/showcase-assertions.json";

interface ShowcaseAssertion {
  levelId: string;
  name: string;
  rounds: number;
  unitsPerTeam?: number;
}

const ALL_LEVELS = Object.keys(assertionsConfig.assertions);

export function renderScreen9ShowcaseValidator(
  app: HTMLElement,
  onNavigate: (path: string) => void
): void {
  app.innerHTML = `
    <div class="s9-root">
      <div class="s9-header">
        <h1 class="s9-title">Showcase Validator</h1>
        <p class="s9-desc">展示关卡回归验证 — P0 观赏性回归 / P1 行为断言 / P2 改进建议</p>
      </div>
      <div class="s9-controls">
        <button class="s9-btn s9-btn-all" id="s9-btn-all">Run All (4 levels)</button>
        ${ALL_LEVELS.map(id => {
          const a = (assertionsConfig.assertions as Record<string, ShowcaseAssertion>)[id];
          return `<button class="s9-btn s9-btn-single" data-level="${id}">${a.name} (${a.unitsPerTeam ?? 1}v${a.unitsPerTeam ?? 1})</button>`;
        }).join("")}
      </div>
      <div class="s9-status" id="s9-status">Click a button to start validation</div>
      <div class="s9-progress" id="s9-progress" hidden></div>
      <div class="s9-results" id="s9-results"></div>
      <button class="s9-back-btn" id="s9-back-btn">回到主页</button>
    </div>
  `;

  // 返回按钮
  app.querySelector("#s9-back-btn")?.addEventListener("click", () => onNavigate(pathFor("armory-hall")));

  // Run All 按钮
  app.querySelector("#s9-btn-all")?.addEventListener("click", () => runValidationAll(app));

  // 单关按钮
  app.querySelectorAll(".s9-btn-single").forEach(btn => {
    btn.addEventListener("click", () => {
      const levelId = (btn as HTMLElement).dataset.level!;
      runValidationSingle(app, levelId);
    });
  });
}

async function runValidationAll(app: HTMLElement): Promise<void> {
  const statusEl = app.querySelector("#s9-status") as HTMLElement;
  const progressEl = app.querySelector("#s9-progress") as HTMLElement;
  const resultsEl = app.querySelector("#s9-results") as HTMLElement;

  // 禁用按钮
  disableButtons(app, true);

  resultsEl.innerHTML = "";
  progressEl.hidden = false;

  const allReports: ShowcaseReport[] = [];
  let overallPassed = true;

  for (let i = 0; i < ALL_LEVELS.length; i++) {
    const levelId = ALL_LEVELS[i];
    const a = (assertionsConfig.assertions as Record<string, ShowcaseAssertion>)[levelId];
    statusEl.textContent = `Running ${i + 1}/${ALL_LEVELS.length}: ${a.name}...`;
    progressEl.textContent = `${"█".repeat(i + 1)}${"░".repeat(ALL_LEVELS.length - i - 1)}`;

    try {
      const report = await validateShowcaseLevel(levelId);
      allReports.push(report);
      if (!report.p0Passed || !report.p1Passed) overallPassed = false;
      appendLevelResult(resultsEl, report, i + 1);
    } catch (err) {
      statusEl.textContent = `Error: ${a.name} - ${(err as Error).message}`;
      appendErrorResult(resultsEl, levelId, (err as Error).message);
    }

    // 让出主线程让 UI 更新
    await delay(50);
  }

  statusEl.textContent = overallPassed ? "All validations PASSED" : "Some validations FAILED";
  statusEl.className = `s9-status s9-${overallPassed ? "pass" : "fail"}`;
  progressEl.hidden = true;

  // 汇总
  appendSummary(resultsEl, allReports, overallPassed);

  disableButtons(app, false);
}

async function runValidationSingle(app: HTMLElement, levelId: string): Promise<void> {
  const statusEl = app.querySelector("#s9-status") as HTMLElement;
  const progressEl = app.querySelector("#s9-progress") as HTMLElement;
  const resultsEl = app.querySelector("#s9-results") as HTMLElement;

  const a = (assertionsConfig.assertions as Record<string, ShowcaseAssertion>)[levelId];
  disableButtons(app, true);

  resultsEl.innerHTML = "";
  progressEl.hidden = false;
  progressEl.textContent = "Running...";
  statusEl.textContent = `Running: ${a.name}`;
  await delay(50);

  try {
    const report = await validateShowcaseLevel(levelId);
    appendLevelResult(resultsEl, report, 1);
    statusEl.textContent = report.p0Passed && report.p1Passed ? "PASSED" : "FAILED";
    statusEl.className = `s9-status ${(report.p0Passed && report.p1Passed) ? "s9-pass" : "s9-fail"}`;
  } catch (err) {
    statusEl.textContent = `Error: ${(err as Error).message}`;
    statusEl.className = "s9-status s9-fail";
  }

  progressEl.hidden = true;
  disableButtons(app, false);
}

function appendLevelResult(container: HTMLElement, report: ShowcaseReport, index: number): void {
  const p0Icon = report.p0Passed ? "✓" : "✗";
  const p1Icon = report.p1Passed ? "✓" : "✗";
  const p0Class = report.p0Passed ? "pass" : "fail";
  const p1Class = report.p1Passed ? "pass" : "fail";

  const div = document.createElement("div");
  div.className = "s9-level-result";
  div.innerHTML = `
    <div class="s9-level-header">
      <span class="s9-level-num">#${index}</span>
      <span class="s9-level-name">${report.levelName}</span>
      <span class="s9-level-id">(${report.levelId})</span>
      <span class="s9-level-time">${(report.duration / 1000).toFixed(1)}s</span>
    </div>
    <div class="s9-level-row">
      <span class="s9-badge ${p0Class}">P0 ${p0Icon}</span>
      Spectacle: ${report.avgSpectacle}/100 (baseline: ${report.baselineSpectacle}/100, threshold: ${report.p0Threshold}/100)
    </div>
    <div class="s9-level-row">
      <span class="s9-badge ${p1Class}">P1 ${p1Icon}</span>
      ${report.p1Passed ? "All behavioral assertions passed" : report.p1Failures.slice(0, 3).join("; ")}
      ${report.p1Failures.length > 3 ? `... and ${report.p1Failures.length - 3} more` : ""}
    </div>
    <div class="s9-level-dims">
      DmgEx=${Math.round(report.avgDimensions.damageExchange)} 
      Move=${Math.round(report.avgDimensions.movementComplexity)} 
      Cover=${Math.round(report.avgDimensions.coverInteraction)} 
      Phase=${Math.round(report.avgDimensions.phaseProgression)} 
      Dur=${Math.round(report.avgDimensions.durationOptimal)} 
      Comeback=${Math.round(report.avgDimensions.lowHpComeback)}
    </div>
    ${report.p2Diagnoses.length > 0 ? `<div class="s9-level-p2">${report.p2Diagnoses.map((d: string) => `<div class="s9-diag-item">${d}</div>`).join("")}</div>` : ""}
  `;
  container.appendChild(div);
}

function appendErrorResult(container: HTMLElement, levelId: string, error: string): void {
  const div = document.createElement("div");
  div.className = "s9-level-result s9-error";
  div.innerHTML = `
    <div class="s9-level-header">
      <span class="s9-level-name">${levelId}</span>
      <span class="s9-badge fail">ERROR</span>
    </div>
    <div class="s9-level-row">${error}</div>
  `;
  container.appendChild(div);
}

function appendSummary(container: HTMLElement, reports: ShowcaseReport[], overallPassed: boolean): void {
  const div = document.createElement("div");
  div.className = `s9-summary ${overallPassed ? "s9-pass" : "s9-fail"}`;
  div.innerHTML = `
    <div class="s9-summary-title">${overallPassed ? "ALL PASSED" : "FAILURES DETECTED"}</div>
    <div class="s9-summary-detail">
      ${reports.length} levels validated · 
      Total: ${reports.reduce((s, r) => s + r.rounds, 0)} rounds · 
      ${reports.reduce((s, r) => s + r.duration, 0) / 1000}s
    </div>
  `;
  container.insertBefore(div, container.firstChild);
}

function disableButtons(app: HTMLElement, disabled: boolean): void {
  app.querySelectorAll(".s9-btn").forEach(btn => {
    (btn as HTMLButtonElement).disabled = disabled;
  });
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
